import { EventSystem, useCurrentRouteState } from '@pbk-web/state';
import { ToastType, useToasts } from '@pbk-web/ui';
import type { AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePbkAuth } from '../contexts/PbkAuthContext';
import { useCurrentUser } from '../global-state/Auth';
import { useErrors } from '../global-state/Errors';
import ErrorType from '../models/Error';
import BaseService from '../services/BaseService';
import { apiErrorMessage } from '../utils/ApiErrorUtils';
import { trimErrorData, trimResponseData } from './trimResponseData';
import useRefreshToken from './useRefreshToken';
import useSentry from './useSentry';

const { UNAUTHORIZED, FORBIDDEN, SERVER_ERROR, SERVICE_UNAVAILABLE, UNKNOWN_ERROR } = ErrorType;

const useErrorInterceptor = () => {
  const { captureApiError } = useSentry();
  const { t } = useTranslation('common');
  const toastsRef = useRef(useToasts());
  const setErrorState = useErrors((x) => x.setValue);
  const refreshToken = useRefreshToken();
  const auth = usePbkAuth();
  const currentRoute = useCurrentRouteState((x) => x.value);

  const interceptorRef = useRef<number | undefined>(-1);

  const setError = useCallback(
    (type: (typeof ErrorType)[keyof typeof ErrorType]) => {
      setErrorState({ type });
    },
    [setErrorState],
  );

  const handleRetryOrError = useCallback(
    async (res: AxiosResponse) => {
      const prevRequest = res?.config;

      // First time this request was made
      if (!prevRequest?.sent) {
        // Handle 401/403 errors
        if ([401, 403].includes(res.status)) {
          // Scriptex flow
          if (auth.features.unmanagedTokens) {
            const refreshPromise = res.status === 401 ? refreshToken(true) : Promise.resolve(true);
            const refreshPromiseResult = await refreshPromise;

            // Successfully refreshed token, retry the original request
            if (refreshPromiseResult) {
              return BaseService.httpClient({ ...prevRequest, sent: true });
            }
            // Something went wrong with refresh token, handle the error
            else {
              setError(res.status === 401 ? UNAUTHORIZED : FORBIDDEN);
            }
          }
          // FusionAuth flow
          else {
            setError(res.status === 401 ? UNAUTHORIZED : FORBIDDEN);
          }
        }
        // 500 request on first try, handle the error
        else if (res.status >= 500) {
          setError(SERVER_ERROR);
        }
      } else {
        // Retried already, still failed
        if (res.status === 401) {
          setError(UNAUTHORIZED);
        } else if (res.status === 403) {
          setError(FORBIDDEN);
        } else {
          setError(SERVER_ERROR);
        }
      }

      return res;
    },
    [auth.features.unmanagedTokens, refreshToken, setError],
  );

  const initInterceptor = useCallback(() => {
    interceptorRef.current = BaseService.httpClient.interceptors.response.use(
      async (res) => {
        if (res.config.url?.includes('/v1/ping') && res.status === 200) {
          return res;
        }

        const ignoredStatuses = res.config.ignoredErrorStatusCodes ?? [];
        const isErrorStatus = [400, 401, 402, 403, 500].includes(res.status);
        const isIgnored = ignoredStatuses.includes(res.status);

        // An ignored 401 still refreshes and retries once; only the takeover is skipped
        if (isIgnored && res.status === 401 && auth.features.unmanagedTokens && !res.config.sent && !res.config.isTokenRefresh) {
          if (await refreshToken(true)) {
            return BaseService.httpClient({ ...res.config, sent: true });
          }
        }

        if (!BaseService.disableInterceptorErrors && isErrorStatus && !isIgnored) {
          if (res.status === 400 || res.status === 402) {
            toastsRef.current.addToast({
              title: t('errors.bad-request.subtitle'),
              type: ToastType.ERROR,
              description: apiErrorMessage(res.data?.meta, t, useCurrentUser.getState().value?.fullName) ?? t('errors.bad-request.description'),
              expiresInMs: 10000,
            });
          } else if (!res.config.isTokenRefresh && !currentRoute?.publicPage) {
            return await handleRetryOrError(res);
          }
        }

        // Network or error response code
        if (res.status === 0 || (res.status >= 400 && res.status !== 404)) {
          // Log network/server errors to sentry
          if (res.status === 0 || res.status >= 500) {
            captureApiError({ response: trimResponseData(res) });
          }

          throw res;
        }

        return res;
      },
      (err) => {
        if (err.config?.url?.includes('/v1/ping')) {
          return Promise.reject(err);
        }

        if (err.code === 'ERR_CANCELED') {
          // Reject with the original axios CanceledError (not a string) so the
          // request-dedup layer's `axios.isCancel()` check still recognizes it and
          // resolves the duplicate with the real request's data, instead of the
          // cancellation surfacing as an unhandled rejection in the caller.
          return Promise.reject(err);
        }

        if (err.response === undefined || err.response.status === 0) {
          if (err.config?.suppressServiceUnavailableTakeover) {
            captureApiError({ error: trimErrorData(err) });
          } else {
            setError(SERVICE_UNAVAILABLE);
          }
        } else {
          setError(UNKNOWN_ERROR);
          captureApiError({ error: err });
        }

        return Promise.reject(err);
      },
    );

    return () => {
      if (interceptorRef.current !== undefined) {
        BaseService.httpClient.interceptors.response.eject(interceptorRef.current);
        interceptorRef.current = undefined;
      }
    };
  }, [auth.features.unmanagedTokens, captureApiError, currentRoute?.publicPage, handleRetryOrError, refreshToken, setError, t, toastsRef]);

  useEffect(() => {
    const handler = () => {
      if (interceptorRef.current !== undefined) {
        BaseService.httpClient.interceptors.response.eject(interceptorRef.current);
        interceptorRef.current = undefined;
      }
      initInterceptor();
    };

    EventSystem.listen('http-client-recreated', handler);

    return () => {
      EventSystem.stopListening('http-client-recreated', handler);
    };
  }, [initInterceptor]);

  useEffect(() => {
    const cleanup = initInterceptor();
    return () => {
      cleanup(); // ensures interceptor is removed
    };
  }, [initInterceptor]);
};

export default useErrorInterceptor;
